import React, { useEffect, useRef, useState } from "react";
import Layout from "../../Layout";
import CategoriesSidebar from "../../components/Services/CategoriesSidebar";
import MobileCategories from "../../components/Services/MobileCategories";
import ServiceCard from "../../components/Services/ServiceCard";
import {
  FileDown,
  FileSpreadsheet,
  FileText,
  Link2,
  ListChecks,
  MoreVertical,
  Settings,
  Upload,
} from "lucide-react";
import DropdownMenuServices from "../../components/Services/DropdownMenuServices";
import ActionsMenu from "../../components/Services/ActionMenu";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import Loading from "../../Util/Loading";
import AddCategoryModal from "../../components/Services/AddCategoryModal";
import { useNavigate } from "react-router-dom";
import ConfirmDeleteModal from "../../components/Services/SingleServiceComponents/ConfirmDeleteModal";
import menuApi from "../../api/menu/menu.api";

const BookingServices = () => {
  const [activeCategory, setActiveCategory] = useState("All categories");
  const [openMenu, setOpenMenu] = useState(null);
  const [activeCategoryId, setActiveCategoryId] = useState("all");

  // console.log({ activeCategoryId });

  const [serviceCategory, setServiceCategory] = useState([]);
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // NEW: category modal state here
  const [openCatModal, setOpenCatModal] = useState(false);
  const [editCat, setEditCat] = useState(null); // null => add, object => edit

  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingId, setPendingId] = useState(null);

  const user = useSelector((state) => state.user.user);
  const navivate = useNavigate();
  const [search, setSearch] = useState("");
  const operator = user;

  const getServicelData = async () => {
    try {
      const operatorId = operator?._id;

      const response = await menuApi.getMenuItemsActiveandInactive({
        operatorId,
      });

      if (response.success) {
        setServices(response.foodItems || []);
      } else {
        toast.error("No data found");
      }
    } catch (error) {
      toast.error("Something went wrong!");
      console.error("Error fetching data:", error);
    }
  };

  const getServiceCategories = async () => {
    try {
      const operatorId = operator?._id;

      const response = await menuApi.getMenuCategories({
        operator: operatorId,
      });

      if (response.success) {
        setServiceCategory(response.categories || []);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  useEffect(() => {
    if (!operator?._id) return;

    setIsLoading(true);

    const loadOperatorData = async () => {
      try {
        await Promise.all([getServicelData(), getServiceCategories()]);
      } catch (err) {
        console.error("Error loading operator data:", err);
      } finally {
        setIsLoading(false); // ✅ remove delay
      }
    };

    loadOperatorData();
  }, [operator]);

  // console.log({ services });
  // console.log({ serviceCategory });

  const categoryRefs = useRef({});
  const categoryButtonRefs = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const categoryId = entry.target.getAttribute("data-category-id");
            if (categoryId) {
              // find category name by ID
              const matchedCat = serviceCategory.find(
                (c) => c._id === categoryId,
              );
              if (matchedCat) {
                setActiveCategory(categoryId); // ✅ use id directly

                const button = categoryButtonRefs.current?.[categoryId];
                if (button) {
                  button.scrollIntoView({
                    behavior: "smooth",
                    inline: "start",
                    block: "nearest",
                  });
                }
              }
            }
          }
        });
      },
      { threshold: 0.4 },
    );

    Object.keys(categoryRefs.current).forEach((id) => {
      if (categoryRefs.current[id]) {
        observer.observe(categoryRefs.current[id]);
      }
    });

    return () => observer.disconnect();
  }, [serviceCategory]);

  const groupedServices = serviceCategory.map((cat) => ({
    _id: cat._id,
    group: cat.name,
    color: cat.color,
    image: cat.image,
    items: services.filter((srv) => srv.category?._id === cat._id),
  }));

  // const filteredServices =
  //   activeCategory === "All categories"
  //     ? groupedServices
  //     : groupedServices.filter((g) => g.group === activeCategory);

  const filteredServices =
    activeCategoryId === "all"
      ? groupedServices
      : groupedServices.filter((g) => g._id === activeCategoryId);

  // 🔥 apply search filter
  const finalServices = filteredServices
    .map((group) => ({
      ...group,
      items: group.items.filter((srv) =>
        srv.title.toLowerCase().includes(search.toLowerCase()),
      ),
    }))
    .filter((group) => group.items.length > 0);

  const categoriesForUI = [
    { _id: "all", name: "All categories", image: null, count: services.length },
    ...serviceCategory.map((cat) => ({
      _id: cat._id,
      name: cat.name,
      image: cat.image,
      color: cat.color,
      count: services.filter((srv) => srv.category?._id === cat._id).length,
    })),
  ];

  // ------- Add/Edit Category handlers ----------
  const openAddCategory = () => {
    setEditCat(null);
    setOpenCatModal(true);
  };

  const openEditCategory = (catLike) => {
    // catLike is from groupedServices (group, _id, color, image, description)

    setEditCat({
      _id: catLike._id,
      name: catLike.group,
      color: catLike.color,
      image: catLike.image,
      description: catLike.description || "",
    });
    setOpenCatModal(true);
  };

  const handleCategoryCreated = (created) => {
    // ignore deleted, normalize shape
    if (!created?._id) return getServiceCategories(); // fallback refetch
    setServiceCategory((prev) => [created, ...prev]);
  };

  const handleCategoryUpdated = (updated) => {
    if (!updated?._id) return getServiceCategories(); // fallback refetch
    setServiceCategory((prev) =>
      prev.map((c) => (c._id === updated._id ? { ...c, ...updated } : c)),
    );
  };

  // define handler
  const handleDeleteCategory = async (categoryId) => {
    try {
      const { response, err } = await menuApi.deleteMenuCategory(categoryId);
      if (err) throw err;

      toast.success("Category deleted");

      // Remove from local state so UI refreshes right away
      setServiceCategory((prev) => prev.filter((c) => c._id !== categoryId));
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to delete category",
      );
    }
  };

  // add single services
  const handleSignleServiceCreated = (categoryId) => {
    if (!categoryId || categoryId === "all") {
      navivate("/food/item/add/new");
    } else {
      navivate(`/food/item/add/new?categoryId=${categoryId}`);
    }
  };

  const handleBunddleServiceCreated = () => {
    navivate("/food/package/add/new");
  };

  const navigate = useNavigate();

  const highlightText = (text, keyword) => {
    if (!keyword || !text) return text;

    const regex = new RegExp(`(${keyword})`, "gi");

    return text.split(regex).map((part, i) =>
      part.toLowerCase() === keyword.toLowerCase() ? (
        <span
          key={i}
          className="bg-yellowstar text-navy px-0 rounded font-semibold"
        >
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  if (isLoading) {
    return <Loading />;
  }
  return (
    <Layout>
      <div className="p-4 md:p-6 w-full container mx-auto min-h-screen">
        <div className="text-text rounded-lg  p-0 lg:p-6 min-h-screen">
          {/* Header */}
          <div className="flex items-start lg:items-center justify-between mb-6">
            <div className="flex flex-col items-start justify-center">
              <h2 className="text-xl font-semibold text-yellowstar animate-neonYellow">
                Service menu
              </h2>
              <p className="text-sm text-text">
                View and manage the services offered by your business.
              </p>
            </div>

            {/* Desktop Buttons */}
            <div className="hidden md:flex gap-2">
              <DropdownMenuServices
                label="Add"
                buttonClass="bg-dry text-text"
                items={[
                  {
                    label: "Single service",
                    onClick: () => handleSignleServiceCreated(activeCategoryId),
                  },
                  { label: "Category", onClick: openAddCategory }, // <-- open Add Category
                ]}
              />
              {/* <button
                onClick={() => navigate("/bookingservices/upload-menu")}
                className="px-3 py-1 border border-border rounded-md text-sm flex items-center gap-1"
              >
                <Upload size={16} />
                AI Upload
              </button> */}
            </div>

            {/* Mobile Buttons */}
            <div className="flex md:hidden gap-2 items-center">
              <DropdownMenuServices
                label="+ Add"
                buttonClass="bg-dry text-text w-[70px]"
                items={[
                  {
                    label: "Single service",
                    onClick: handleSignleServiceCreated,
                  },
                  { label: "Category", onClick: openAddCategory }, // <-- open Add Category (mobile)
                ]}
              />

              {/* <button
                onClick={() => navigate("/bookingservices/upload-menu")}
                className="px-3 py-1 border border-border rounded-md text-sm flex items-center gap-1"
              >
                <Upload size={16} /> AI
              </button> */}
            </div>
          </div>

          {/* Search + Filter Row */}
          <div className="flex gap-3 mb-6">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search service name"
              className="flex-1 border border-gray-400 rounded-md px-3 py-2 text-sm bg-dry placeholder-text-gray"
            />
          </div>

          {/* Mobile Categories Pills */}
          <MobileCategories
            categories={categoriesForUI}
            categoryButtonRefs={categoryButtonRefs}
            activeCategoryId={activeCategoryId}
            setActiveCategoryId={setActiveCategoryId}
          />

          {/* Two Column Layout */}
          <div className="flex gap-6">
            <CategoriesSidebar
              categories={categoriesForUI}
              categoryButtonRefs={categoryButtonRefs}
              onCategoryCreated={handleCategoryCreated}
              onCategoryUpdated={handleCategoryUpdated}
              activeCategoryId={activeCategoryId}
              setActiveCategoryId={setActiveCategoryId}
              operatorId={user?._id}
            />

            {/* Services List */}
            <div className="flex-1 max-h-[70vh] overflow-y-auto scrollbar-thin">
              {/* {filteredServices.map((group) => ( */}
              {finalServices.map((group) => (
                <div key={group._id} className="mb-10">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {group.image && (
                        <img
                          src={group.image}
                          alt={group.group}
                          className="w-20 h-12 object-cover rounded"
                        />
                      )}
                      <h3
                        className="text-lg font-semibold"
                        style={{ color: group.color }}
                      >
                        {group.group}
                      </h3>
                    </div>

                    {/* Actions menu controls the modal */}
                    <ActionsMenu
                      items={[
                        {
                          label: "Edit",
                          onClick: () => openEditCategory(group),
                        },
                        {
                          label: "Add service",
                          onClick: () => handleSignleServiceCreated(group._id),
                        },

                        {
                          label: "Permanently delete",
                          danger: true,
                          onClick: () => {
                            setPendingId(group._id); // store which category
                            setShowConfirm(true); // open modal
                          },
                        },
                      ]}
                    />
                  </div>

                  <div className="space-y-3">
                    {group.items.map((srv) => (
                      <ServiceCard
                        key={srv._id || srv.title}
                        srv={srv}
                        search={search}
                        highlightText={highlightText} // ✅ pass function
                        color={group.color}
                        openMenu={openMenu}
                        setOpenMenu={setOpenMenu}
                        onDeleted={(id) =>
                          setServices((prev) =>
                            prev.filter((s) => s._id !== id),
                          )
                        }
                        onDuplicated={
                          (newService) =>
                            setServices((prev) => [...prev, newService]) // ✅ add duplicate at top
                        }
                        onUpdated={(id, newStatus) =>
                          setServices((prev) =>
                            prev.map((s) =>
                              s._id === id ? { ...s, isActive: newStatus } : s,
                            ),
                          )
                        }
                      />
                    ))}
                  </div>
                </div>
              ))}

              {finalServices.length === 0 && (
                <p className="text-gray-500 text-center mt-10">
                  No services found for "{search}"
                </p>
              )}

              {filteredServices.length === 0 && (
                <p className="text-gray-500">
                  No services found in this category.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <ConfirmDeleteModal
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={() => handleDeleteCategory(pendingId)}
      />

      {/* One shared Add/Edit Category modal */}
      <AddCategoryModal
        open={openCatModal}
        category={editCat} // null => Add, object => Edit
        onClose={() => {
          setOpenCatModal(false);
          setEditCat(null);
        }}
        onCreated={handleCategoryCreated}
        onUpdated={handleCategoryUpdated}
        operatorId={user?._id}
      />
    </Layout>
  );
};

export default BookingServices;
